import { useState, useCallback, useEffect, useRef } from 'react';
import { Company, User } from '../types';
import { getPostgresClient } from '../services/postgresClient';

export interface UseAuthSessionParams {
  /** Carrega dados da empresa após login bem-sucedido; retorna false se falhar. */
  onLoginSuccess: (user: User, company: Company) => Promise<boolean>;
  /** Limpa estado do board ao sair. */
  onLogout: () => void;
  /** URL da API Postgres (mesma origem se vazia). */
  postgresApiUrl?: string;
}

/**
 * Sessão de autenticação: seleção de empresa, modal de login, logout e restauração via API.
 *
 * Após F5, restaura usuário + permissões + empresa ativa exclusivamente via
 * GET /auth/session (cookie httpOnly) — sem sessionStorage de identidade.
 */
export function useAuthSession({
  onLoginSuccess,
  onLogout,
  postgresApiUrl = '',
}: UseAuthSessionParams) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentCompany, setCurrentCompany] = useState<Company | null>(null);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  /** true enquanto valida sessão no servidor (evita piscar LoginView no F5). */
  const [isRestoringSession, setIsRestoringSession] = useState(true);

  const onLoginSuccessRef = useRef(onLoginSuccess);
  useEffect(() => {
    onLoginSuccessRef.current = onLoginSuccess;
  });

  /**
   * Restaura board quando perfil de sessão tem usuário e empresa ativa.
   * @param user - Usuário com permissões atualizadas do servidor
   * @param company - Empresa ativa persistida na sessão
   */
  const restoreSessionBoard = useCallback(async (user: User, company: Company): Promise<boolean> => {
    setCurrentUser(user);
    setCurrentCompany(company);
    setIsLoading(true);
    try {
      const ok = await onLoginSuccessRef.current(user, company);
      if (ok) {
        setIsAuthenticated(true);
        setIsAuthModalOpen(false);
      }
      return ok;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleSelectCompany = useCallback(
    async (company: Company) => {
      setCurrentCompany(company);
      const sb = getPostgresClient(postgresApiUrl);
      const { data: profile } = await sb.getSessionProfile();

      if (profile?.user?.allowedCompanies.includes(company)) {
        const { error } = await sb.setActiveCompany(company);
        if (error) {
          setIsAuthModalOpen(true);
          return;
        }
        await restoreSessionBoard(profile.user, company);
        return;
      }

      setIsAuthModalOpen(true);
    },
    [postgresApiUrl, restoreSessionBoard],
  );

  const handleAuthSuccess = useCallback(
    async (user: User) => {
      if (!currentCompany) return;

      const sb = getPostgresClient(postgresApiUrl);
      const { error: companyError } = await sb.setActiveCompany(currentCompany);
      if (companyError) return;

      const { data: profile } = await sb.getSessionProfile();
      const freshUser = profile?.user ?? user;

      setIsLoading(true);
      try {
        const ok = await onLoginSuccessRef.current(freshUser, currentCompany);
        if (!ok) return;
        setCurrentUser(freshUser);
        setIsAuthModalOpen(false);
        setIsAuthenticated(true);
      } finally {
        setIsLoading(false);
      }
    },
    [currentCompany, postgresApiUrl],
  );

  const handleLogout = useCallback(() => {
    const sb = getPostgresClient(postgresApiUrl);
    void sb.auth.signOut().finally(() => {
      onLogout();
      setIsAuthenticated(false);
      setCurrentCompany(null);
      setCurrentUser(null);
    });
  }, [onLogout, postgresApiUrl]);

  /** Restaura sessão após F5 via GET /auth/session (cookie + empresa ativa no servidor). */
  useEffect(() => {
    if (isAuthenticated) {
      setIsRestoringSession(false);
      return;
    }

    const sb = getPostgresClient(postgresApiUrl);
    void sb.getSessionProfile().then(async ({ data: profile }) => {
      if (profile?.user && profile.activeCompany) {
        await restoreSessionBoard(profile.user, profile.activeCompany);
      }
      setIsRestoringSession(false);
    });
  }, [postgresApiUrl, isAuthenticated, restoreSessionBoard]);

  return {
    isAuthenticated,
    isRestoringSession,
    currentCompany,
    currentUser,
    isAuthModalOpen,
    setIsAuthModalOpen,
    isLoading,
    handleSelectCompany,
    handleAuthSuccess,
    handleLogout,
    setCurrentUser,
  };
};
